import { X } from 'lucide-react';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import TouchSpin from '~/components/TouchSpin';
import type { ApiProductCustomization, CustomizationPayloadItem } from '~/types/Product';
import { useFormatPrice } from '~/utils/price';

export type { CustomizationPayloadItem };

interface Props {
  customizations: ApiProductCustomization[];
  basePrice: number | undefined;
  basePriceWithoutVat?: number | null;
  currency: string | undefined;
  vatRate?: number;
  onChange: (_selections: CustomizationPayloadItem[]) => void;
  onImageHover?: (_imageUrl: string | null) => void;
}

const ProductCustomizations: React.FC<Props> = ({
  customizations,
  basePrice,
  basePriceWithoutVat,
  currency,
  vatRate = 21,
  onChange,
  onImageHover,
}) => {
  const { t } = useTranslation();
  const formatPrice = useFormatPrice();

  const sortedCustomizations = useMemo(
    () => [...customizations].sort((a, b) => a.position - b.position),
    [customizations]
  );

  const [componentSelections, setComponentSelections] = useState<Record<number, number | null>>(() => {
    const init: Record<number, number | null> = {};
    for (const c of sortedCustomizations) {
      if (c.type === 'component') {
        init[c.id] = c.items.length > 0 ? c.items[0].id : null;
      }
    }
    return init;
  });

  const [addonSelections, setAddonSelections] = useState<Record<number, number[]>>(() => {
    const init: Record<number, number[]> = {};
    for (const c of sortedCustomizations) {
      if (c.type === 'addon') {
        init[c.id] = [];
      }
    }
    return init;
  });

  const [addonQuantities, setAddonQuantities] = useState<Record<number, Record<number, number>>>({});
  const [modalImage, setModalImage] = useState<{ src: string; label: string } | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 991.98px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const visibleCustomizations = useMemo(() => {
    const byId = new Map<number, ApiProductCustomization>();
    for (const c of sortedCustomizations) byId.set(c.id, c);

    const cache = new Map<number, boolean>();
    const inProgress = new Set<number>();

    // A customization is visible when all of its conditions are satisfied.
    // A condition can only be satisfied if the customization it targets is
    // itself visible — so visibility cascades: if a targeted customization is
    // hidden, everything depending on it is hidden too (to any depth).
    const isVisible = (c: ApiProductCustomization): boolean => {
      const cached = cache.get(c.id);
      if (cached !== undefined) return cached;
      if (c.conditions.length === 0) {
        cache.set(c.id, true);
        return true;
      }
      // Guard against circular conditions.
      if (inProgress.has(c.id)) return false;
      inProgress.add(c.id);

      const result = c.conditions.every((cond) => {
        const target = byId.get(cond.targetCategoryId);
        if (!target || !isVisible(target)) return false;
        if (cond.rule === 'contains') {
          const compSel = componentSelections[cond.targetCategoryId];
          if (compSel !== undefined && compSel !== null && cond.selectedItemIds.includes(compSel)) {
            return true;
          }
          const addSel = addonSelections[cond.targetCategoryId] ?? [];
          if (addSel.some((id) => cond.selectedItemIds.includes(id))) {
            return true;
          }
        }
        return false;
      });

      inProgress.delete(c.id);
      cache.set(c.id, result);
      return result;
    };

    return sortedCustomizations.filter((c) => isVisible(c));
  }, [sortedCustomizations, componentSelections, addonSelections]);

  useEffect(() => {
    const payload: CustomizationPayloadItem[] = [];
    for (const c of visibleCustomizations) {
      if (c.type === 'component') {
        const selectedId = componentSelections[c.id];
        if (selectedId !== null && selectedId !== undefined) {
          payload.push({ categoryId: c.id, itemId: selectedId, quantity: 1 });
        }
      } else {
        const selectedIds = addonSelections[c.id] ?? [];
        for (const itemId of selectedIds) {
          const qty = addonQuantities[c.id]?.[itemId] ?? 1;
          payload.push({ categoryId: c.id, itemId, quantity: qty });
        }
      }
    }
    onChange(payload);
  }, [visibleCustomizations, componentSelections, addonSelections, addonQuantities, onChange]);

  const additionalPrice = useMemo(() => {
    let total = 0;
    for (const c of visibleCustomizations) {
      if (c.type === 'component') {
        const selectedId = componentSelections[c.id];
        const item = c.items.find((i) => i.id === selectedId);
        if (typeof item?.price === 'number') total += item.price;
      } else {
        const selectedIds = addonSelections[c.id] ?? [];
        for (const itemId of selectedIds) {
          const item = c.items.find((i) => i.id === itemId);
          const qty = addonQuantities[c.id]?.[itemId] ?? 1;
          if (typeof item?.price === 'number') total += item.price * qty;
        }
      }
    }
    return total;
  }, [visibleCustomizations, componentSelections, addonSelections, addonQuantities]);

  // Net (excl. VAT) of the selected options, summed from the server-provided
  // priceWithoutVat so the rate always matches what is configured in admin
  // (never a hardcoded 21%). Null when any selected item lacks a net price,
  // in which case the summary falls back to the vatRate divisor below.
  const additionalPriceWithoutVat = useMemo(() => {
    let total = 0;
    for (const c of visibleCustomizations) {
      if (c.type === 'component') {
        const selectedId = componentSelections[c.id];
        const item = c.items.find((i) => i.id === selectedId);
        if (item && typeof item.price === 'number') {
          if (typeof item.priceWithoutVat !== 'number') return null;
          total += item.priceWithoutVat;
        }
      } else {
        const selectedIds = addonSelections[c.id] ?? [];
        for (const itemId of selectedIds) {
          const item = c.items.find((i) => i.id === itemId);
          const qty = addonQuantities[c.id]?.[itemId] ?? 1;
          if (item && typeof item.price === 'number') {
            if (typeof item.priceWithoutVat !== 'number') return null;
            total += item.priceWithoutVat * qty;
          }
        }
      }
    }
    return total;
  }, [visibleCustomizations, componentSelections, addonSelections, addonQuantities]);

  // Prefer exact server-provided net values; fall back to the legacy vatRate
  // divisor only when net data is unavailable.
  const totalPriceWithoutVat =
    basePrice !== undefined && typeof basePriceWithoutVat === 'number' && additionalPriceWithoutVat !== null
      ? basePriceWithoutVat + additionalPriceWithoutVat
      : basePrice !== undefined
        ? Math.round((basePrice + additionalPrice) / (1 + vatRate / 100))
        : 0;

  const handleComponentChange = useCallback((categoryId: number, itemId: number) => {
    setComponentSelections((prev) => ({ ...prev, [categoryId]: itemId }));
  }, []);

  const handleAddonToggle = useCallback((categoryId: number, itemId: number, checked: boolean) => {
    setAddonSelections((prev) => {
      const current = prev[categoryId] ?? [];
      return {
        ...prev,
        [categoryId]: checked ? [...current, itemId] : current.filter((id) => id !== itemId),
      };
    });
  }, []);

  const handleAddonQuantityChange = useCallback((categoryId: number, itemId: number, qty: number) => {
    setAddonQuantities((prev) => ({
      ...prev,
      [categoryId]: { ...(prev[categoryId] ?? {}), [itemId]: qty },
    }));
  }, []);

  return (
    <div className="product-customizations">
      <div className="product-customizations__header">{t('components.product.configurator_title')}</div>

      {visibleCustomizations.map((customization) => {
        return (
          <div key={customization.id} className="product-customizations__group">
            {customization.labelVisible && (
              <div className="product-customizations__group-label">{customization.label}</div>
            )}

            {customization.items.map((item) => {
              const isComponent = customization.type === 'component';
              const isChecked = isComponent
                ? componentSelections[customization.id] === item.id
                : (addonSelections[customization.id] ?? []).includes(item.id);

              const inputId = `cust-${customization.id}-${item.id}`;
              const showQty = item.quantitySelectable && !isComponent && isChecked;

              // Zoom behaviour is shared by the thumbnail and the item name: hover
              // previews the full image on desktop, tap opens it in a modal on mobile.
              const isZoomable = Boolean(item.image);
              const handleZoomEnter = () => {
                if (isMobile) return;
                onImageHover?.(item.image ?? null);
              };
              const handleZoomLeave = () => {
                if (isMobile) return;
                onImageHover?.(null);
              };
              const handleZoomClick = (e: React.MouseEvent) => {
                if (!isMobile) return;
                // Only the image opens the preview; taps anywhere else in the row
                // (name, gap around the radio, padding) select the option as usual.
                const target = e.target as HTMLElement;
                if (!target.closest('.product-customizations__item-image')) return;
                e.preventDefault();
                e.stopPropagation();
                setModalImage({ src: item.image as string, label: item.label });
              };

              return (
                <div
                  key={item.id}
                  className={`product-customizations__item${showQty ? ' product-customizations__item--has-qty' : ''}`}
                >
                  <div className="product-customizations__item-left">
                    <label
                      htmlFor={inputId}
                      className="product-customizations__item-clickable"
                      onMouseEnter={isZoomable ? handleZoomEnter : undefined}
                      onMouseLeave={isZoomable ? handleZoomLeave : undefined}
                      onClick={isZoomable ? handleZoomClick : undefined}
                    >
                      <input
                        id={inputId}
                        type={isComponent ? 'radio' : 'checkbox'}
                        className={`product-customizations__control product-customizations__control--${isComponent ? 'radio' : 'checkbox'}`}
                        name={isComponent ? `customization-${customization.id}` : undefined}
                        checked={isChecked}
                        onChange={(e) => {
                          if (isComponent) {
                            handleComponentChange(customization.id, item.id);
                          } else {
                            handleAddonToggle(customization.id, item.id, e.target.checked);
                          }
                        }}
                      />
                      {item.image && (
                        <img
                          src={item.thumbnail || item.image}
                          alt={item.label}
                          className="product-customizations__item-image"
                        />
                      )}
                      <div className="product-customizations__item-text">
                        <span className="product-customizations__item-label">{item.label}</span>
                      </div>
                    </label>
                  </div>
                  <div className="product-customizations__item-qty">
                    {showQty && (
                      <TouchSpin
                        value={addonQuantities[customization.id]?.[item.id] ?? 1}
                        onChange={(qty) => handleAddonQuantityChange(customization.id, item.id, qty)}
                        min={1}
                        max={99}
                      />
                    )}
                  </div>
                  <span className="product-customizations__item-price">
                    {!item.hidePrice && item.price !== null
                      ? `${item.price > 0 ? '+ ' : ''}${formatPrice(item.price, currency)}`
                      : ''}
                  </span>
                </div>
              );
            })}

            {customization.note && <div className="product-customizations__group-note">{customization.note}</div>}
          </div>
        );
      })}

      <div className="product-customizations__summary">
        {basePrice !== undefined && (
          <div className="product-customizations__summary-row">
            <span>{t('components.product.base_price')}</span>
            <span>{formatPrice(basePrice, currency)}</span>
          </div>
        )}
        <div className="product-customizations__summary-row">
          <span>{t('components.product.additional_equipment')}</span>
          <span>{formatPrice(additionalPrice, currency)}</span>
        </div>
        {basePrice !== undefined && (
          <div className="product-customizations__summary-row">
            <span>{t('components.product.total_price_excl_vat')}</span>
            <span>{formatPrice(totalPriceWithoutVat, currency)}</span>
          </div>
        )}
        {basePrice !== undefined && (
          <div className="product-customizations__summary-row product-customizations__summary-row--total">
            <span>{t('components.product.total_price')}</span>
            <span>{formatPrice(basePrice + additionalPrice, currency)}</span>
          </div>
        )}
      </div>

      <Modal
        show={modalImage !== null}
        onHide={() => setModalImage(null)}
        centered
        size="lg"
        contentClassName="product-customizations__modal-content"
        backdropClassName="product-customizations__modal-backdrop"
      >
        {modalImage && (
          <>
            <button
              type="button"
              className="product-customizations__modal-close"
              onClick={() => setModalImage(null)}
              aria-label={t('common.close')}
            >
              <X size={24} />
            </button>
            <img src={modalImage.src} alt={modalImage.label} className="product-customizations__modal-image" />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ProductCustomizations;
